import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';

import { toastrError, toastrSuccess } from '@portals/redux/actions/toastr';
import {
  PaginatedQueryFilterType,
  PaginationResponse,
  ProductPricingModel,
  UsePaginatedTableApiQuery,
} from '@portals/types';

import {
  STORE_LISTINGS_API_URL,
  storeListingsQueryKeys,
} from './store-listings.constants';
import { StoreListing, StoreListingPrice } from './store-listings.types';
import { useApiQuery } from '../../hooks';
import { ServerError } from '../../types';
import {
  buildUrlFromFilters,
  fetchApiRequest,
  usePaginatedTableApiQuery,
  useRequestOptions,
} from '../../utils';

export function useStoreListings(
  tableState: UsePaginatedTableApiQuery<StoreListing>['tableState'],
  columns: UsePaginatedTableApiQuery<StoreListing>['columns'],
  baseUrl = STORE_LISTINGS_API_URL
) {
  return usePaginatedTableApiQuery<StoreListing>({
    baseUrl,
    columns,
    tableState,
    queryKey: [...storeListingsQueryKeys.all, baseUrl, tableState],
  });
}

export function useStoreListing(storeListingId: string, isEnabled: boolean) {
  return useApiQuery<StoreListing>(
    `${STORE_LISTINGS_API_URL}/${storeListingId}`,
    storeListingsQueryKeys.byId(storeListingId),
    { enabled: isEnabled }
  );
}

export interface CreateStoreListing {
  productId: string;
  prices: Array<StoreListingPrice>;
}
export function useCreateStoreListing() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { url, options } = useRequestOptions({
    url: STORE_LISTINGS_API_URL,
    method: 'POST',
  });

  return useMutation<StoreListing, ServerError, CreateStoreListing>({
    mutationFn: ({ productId, prices }) => {
      return fetchApiRequest(url, {
        ...options,
        body: JSON.stringify({
          product_id: productId,
          prices,
        }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(storeListingsQueryKeys.all);
    },
    onError: ({ error }: any) => {
      dispatch(toastrError(error));
    },
  });
}

interface UseUpdateStoreListingPrices {
  storeListingId: string;
  prices: Array<StoreListingPrice>;
}
export function useUpdateStoreListingPrices() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();

  const { url, options } = useRequestOptions({
    url: STORE_LISTINGS_API_URL,
    method: 'PATCH',
  });

  return useMutation<StoreListing, ServerError, UseUpdateStoreListingPrices>({
    mutationFn: ({ storeListingId, prices }: UseUpdateStoreListingPrices) => {
      return fetchApiRequest(`${url}/${storeListingId}/update_prices`, {
        ...options,
        body: JSON.stringify({ prices }),
      });
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries(storeListingsQueryKeys.all);
    },
    onError: ({ error }: any) => {
      dispatch(toastrError(error));
    },
  });
}

interface UsePublishStoreListingParams {
  storeListingId: string;
}
export function usePublishStoreListing() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: STORE_LISTINGS_API_URL,
    method: 'POST',
  });

  return useMutation<StoreListing, ServerError, UsePublishStoreListingParams>({
    mutationFn: ({ storeListingId }) => {
      return fetchApiRequest(`${url}/${storeListingId}/publish`, options);
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Product published successfully'));

      queryClient.invalidateQueries(storeListingsQueryKeys.all);
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
  });
}

export function useNonePaginatedStoreListings(params?: {
  filters: Array<PaginatedQueryFilterType<StoreListing>>;
}) {
  const { url, options } = useRequestOptions({ url: STORE_LISTINGS_API_URL });

  const requestUrl = buildUrlFromFilters({
    url,
    filters: params?.filters,
    pagination: {
      page: 0,
      pageSize: 1000,
    },
  });

  return useQuery<
    PaginationResponse<StoreListing>,
    ServerError,
    StoreListing[]
  >({
    queryKey: [...storeListingsQueryKeys.all, JSON.stringify(params?.filters)],
    queryFn: () => fetchApiRequest(requestUrl, options),
    select: (response) => {
      // Workaround to filter out personalized products.
      // We cannot filter nested fields via API
      return response.data.filter(
        (storeListing) =>
          storeListing.product.pricing_model !==
          ProductPricingModel.Personalized
      );
    },
  });
}

export function useDeleteStoreListing() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: STORE_LISTINGS_API_URL,
    method: 'DELETE',
  });

  return useMutation<void, ServerError, { storeListingId: string }>({
    mutationFn: ({ storeListingId }) => {
      return fetchApiRequest(`${url}/${storeListingId}`, options);
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Product deleted successfully'));

      queryClient.invalidateQueries(storeListingsQueryKeys.all);
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
  });
}

export function useArchiveStoreListing() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const { url, options } = useRequestOptions({
    url: STORE_LISTINGS_API_URL,
    method: 'POST',
  });

  return useMutation<void, ServerError, { storeListingId: string }>({
    mutationFn: ({ storeListingId }) => {
      return fetchApiRequest(`${url}/${storeListingId}/archive`, options);
    },
    onSuccess: () => {
      dispatch(toastrSuccess('Product archived successfully'));

      queryClient.invalidateQueries(storeListingsQueryKeys.all);
    },
    onError: ({ error }) => {
      dispatch(toastrError(error));
    },
  });
}
